/* AdminRowActions — iter101.46
 *
 * Universal row-level action cluster (Enable/Disable + Impersonate) for
 * the admin Doctors and Patients tables. Modeled after the identical
 * cluster on `/admin/coe` (People tab) so every admin surface reads the
 * same visual grammar:
 *   • Enable/Disable icon — state-aware (rose UserMinus when active,
 *     emerald UserPlus when disabled). Fires the shared `CommentRequiredDialog`
 *     with a ≥10-char reason so the audit trail always records WHY.
 *   • Impersonate icon — opens the target's portal in a NEW TAB via
 *     `openImpersonationInNewTab`. Auto-disables when the target is
 *     currently deactivated.
 *
 * Kept lightweight (two 28×28 icon buttons) so it never dominates the
 * row. `dataTestidPrefix` scopes the test-ids to the caller ("doctor",
 * "patient", etc.) so the two surfaces don't collide in Playwright.
 */
import React, { useState } from "react";
import { UserMinus, UserPlus, SignIn } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CommentRequiredDialog } from "@/components/admin/CommentRequiredDialog";
import { openImpersonationInNewTab } from "@/lib/impersonation";


export default function AdminRowActions({
    // What kind of row this is (drives the API path + test-ids).
    kind,               // "doctor" | "patient"
    // Row identity for the API + audit trail.
    targetId,           // doctor.id  OR  patient.id
    targetUserId,       // doctor.user_id OR patient user id (impersonation only)
    targetName,
    isActive = true,
    // Copy tweaks shown inside the CommentRequiredDialog.
    kindLabel,          // "doctor" | "patient" — falls back to `kind`
    // Fires after a successful toggle so the parent can refresh its list.
    onChanged,
    // Optional wrapping div className (e.g. spacing).
    className = "flex items-center gap-0.5",
    dataTestidPrefix,
}) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const label = kindLabel || kind;
    const testidPrefix = dataTestidPrefix || `admin-${kind}`;

    const doToggle = async (comment) => {
        try {
            await api.put(`/admin/${kind}s/${targetId}/toggle-active`, { comment });
            toast.success(
                isActive
                    ? `${targetName || label} deactivated`
                    : `${targetName || label} activated`
            );
            setDialogOpen(false);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
            throw e;
        }
    };

    const canImpersonate = !!targetUserId;
    const impersonateDisabled = !isActive;

    return (
        <div className={className}>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDialogOpen(true);
                }}
                title={isActive ? `Deactivate ${targetName || label}` : `Activate ${targetName || label}`}
                aria-label={isActive ? `Deactivate ${targetName || label}` : `Activate ${targetName || label}`}
                data-testid={`${testidPrefix}-toggle-${targetId}`}
                className={`w-7 h-7 grid place-items-center rounded-md transition ${
                    isActive
                        ? "text-rose-700 hover:text-rose-900 hover:bg-rose-100"
                        : "text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100"
                }`}
            >
                {isActive ? <UserMinus size={13} weight="bold" /> : <UserPlus size={13} weight="bold" />}
            </button>
            {canImpersonate && (
                <button
                    type="button"
                    title={impersonateDisabled
                        ? `${label} is deactivated — reactivate first to impersonate`
                        : `Open ${targetName || label}'s portal in a new tab`}
                    aria-label={`Impersonate ${targetName || label}`}
                    disabled={impersonateDisabled}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openImpersonationInNewTab({ targetUserId, targetName });
                    }}
                    className="w-7 h-7 grid place-items-center rounded-md text-primary/70 hover:text-primary hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    data-testid={`${testidPrefix}-impersonate-${targetId}`}
                >
                    <SignIn size={13} weight="bold" />
                </button>
            )}
            <CommentRequiredDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title={isActive ? `Deactivate ${targetName || label}?` : `Activate ${targetName || label}?`}
                description={
                    isActive
                        ? `They will lose access immediately. Records, cases, and history are preserved. Please note the reason for deactivation.`
                        : `They will regain access immediately and can resume using their portal. Please note the reason for reactivation.`
                }
                ctaLabel={isActive ? `Deactivate ${label}` : `Activate ${label}`}
                ctaVariant={isActive ? "destructive" : "default"}
                onConfirm={doToggle}
            />
        </div>
    );
}
